import { translateBackendError } from '../utils/labels.ts'
import { beginSessionExpiry, resetSessionExpiry } from '../auth/sessionExpiry.ts'

const BASE = '/api'

export class ApiError extends Error {
  status: number
  rawDetail: string
  conflictHint?: { actor?: string; when?: string }
  constructor(status: number, rawDetail: string, message: string, conflictHint?: { actor?: string; when?: string }) {
    super(message)
    this.status = status
    this.rawDetail = rawDetail
    this.conflictHint = conflictHint
  }
}

export interface ApiResponse<T> {
  data: T
  headers: Headers
  status: number
}

async function requestWithMeta<T>(path: string, options: RequestInit & { timeoutMs?: number } = {}): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  }

  // per-call timeout via AbortController; 默认无超时, 批量端点显式传 timeoutMs=90000
  const { timeoutMs, ...fetchOpts } = options
  const externalSignal = options.signal
  let signal = externalSignal
  let timer: number | undefined
  let timeoutCtrl: AbortController | undefined
  let removeExternalAbortListener: (() => void) | undefined
  if (timeoutMs && timeoutMs > 0) {
    timeoutCtrl = new AbortController()
    if (externalSignal) {
      const abortFromCaller = () => timeoutCtrl?.abort(externalSignal.reason)
      if (externalSignal.aborted) abortFromCaller()
      else {
        externalSignal.addEventListener('abort', abortFromCaller, { once: true })
        removeExternalAbortListener = () => externalSignal.removeEventListener('abort', abortFromCaller)
      }
    }
    timer = window.setTimeout(() => timeoutCtrl?.abort(), timeoutMs)
    signal = timeoutCtrl.signal
  }

  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, { ...fetchOpts, headers, credentials: 'include', signal })
  } catch (e) {
    if (timer !== undefined) clearTimeout(timer)
    if ((e as Error).name === 'AbortError' && !externalSignal?.aborted) {
      throw new ApiError(0, 'timeout', '请求超时, 请稍后重试')
    }
    throw e
  } finally {
    if (timer !== undefined) clearTimeout(timer)
    removeExternalAbortListener?.()
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }))
    const rawDetail = typeof err.detail === 'string' ? err.detail : (err.detail ? JSON.stringify(err.detail) : res.statusText)
    if (res.status === 401) {
      if (path === '/auth/login') {
        throw new ApiError(401, rawDetail, translateBackendError(rawDetail))
      }
      beginSessionExpiry()
      throw new ApiError(401, rawDetail, '登录已过期, 请重新登录')
    }
    if (res.status === 409) {
      const actor = err.last_modified_by_name || err.last_modified_by || err.modified_by
      const when = err.last_modified_at || err.modified_at
      const cn = path.includes('/atmedia-credentials/')
        ? translateBackendError(rawDetail)
        : path.startsWith('/media-todos/')
        ? translateBackendError(rawDetail)
        : actor
        ? `这条记录刚被 ${actor}${when ? ' (' + when + ')' : ''} 改过, 请刷新后重试`
        : '这条记录刚被其他人改过, 请刷新页面重试'
      throw new ApiError(409, rawDetail, cn, { actor, when })
    }
    throw new ApiError(res.status, rawDetail, translateBackendError(rawDetail))
  }
  if (path === '/auth/login') resetSessionExpiry()
  return {
    data: await res.json(),
    headers: res.headers,
    status: res.status,
  }
}

async function request<T>(path: string, options: RequestInit & { timeoutMs?: number } = {}): Promise<T> {
  return (await requestWithMeta<T>(path, options)).data
}

export const api = {
  get: <T>(path: string, opts?: { timeoutMs?: number; signal?: AbortSignal }) =>
    request<T>(path, { ...opts }),
  getWithMeta: <T>(path: string, opts?: { timeoutMs?: number; signal?: AbortSignal }) =>
    requestWithMeta<T>(path, { ...opts }),
  post: <T>(
    path: string,
    body?: unknown,
    opts?: { timeoutMs?: number; signal?: AbortSignal; cache?: RequestCache },
  ) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined, ...opts }),
  patch: <T>(path: string, body?: unknown, opts?: { timeoutMs?: number }) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined, ...opts }),
  put: <T>(path: string, body?: unknown, opts?: { timeoutMs?: number }) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined, ...opts }),
  delete: <T>(path: string, opts?: { timeoutMs?: number }) =>
    request<T>(path, { method: 'DELETE', ...opts }),
}
